import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

// Pinned, not @latest: this is third-party JS on the critical path of this
// section — an upstream release must not be able to break it unannounced.
const CALENDAR_VERSION = "2.3.4";
const SCRIPT_SRC =
  `https://unpkg.com/github-calendar@${CALENDAR_VERSION}/dist/github-calendar.min.js`;
const STYLE_HREF =
  `https://unpkg.com/github-calendar@${CALENDAR_VERSION}/dist/github-calendar-responsive.css`;

// Card washes are tints of the live focus color, never fixed hues: the stops
// are rgba(var(--text-rgb)/var(--accent-rgb), …), so each card re-tones with
// the page and the type on it stays the page's type. Cards are told apart by
// angle and ink/accent mix, not by having their own palette.
const PROJECTS = [
  {
    title: "Vitaelum",
    category: "Mobile",
    desc: "Personal focus operating system — syncs iPhone Focus states to the web in real time.",
    route: "/projects/vitaelum",
    art: "linear-gradient(200deg, rgba(var(--text-rgb), 0.14) 0%, rgba(var(--text-rgb), 0.02) 72%)",
  },
  {
    title: "JobMatch",
    category: "Web",
    desc: "Matching platform connecting candidates with roles that actually fit.",
    route: "/projects/jobmatch",
    art: "linear-gradient(155deg, rgba(var(--text-rgb), 0.17) 0%, rgba(var(--accent-rgb), 0.08) 60%, rgba(var(--text-rgb), 0.02) 100%)",
  },
  {
    title: "Nirilium",
    category: "E-commerce",
    desc: "E-commerce shop in the making — storefront, catalog, checkout.",
    route: "/projects/nirilium",
    art: "linear-gradient(210deg, rgba(var(--accent-rgb), 0.22) 0%, rgba(var(--text-rgb), 0.03) 70%)",
  },
  {
    title: "ArenaV2",
    category: "Volunteer",
    // draft copy — edit freely with the real ArenaV2 details
    desc: "Volunteer development work on the ArenaV2 platform.",
    route: "/projects/arenav2",
    art: "linear-gradient(140deg, rgba(var(--text-rgb), 0.04) 0%, rgba(var(--accent-rgb), 0.16) 100%)",
  },
];

const FILTERS = ["All", "Web", "Mobile", "E-commerce", "Volunteer"];

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const calendarRef = useRef(null);
  const fieldRef = useRef(null);

  useEffect(() => {
    // one calendar is a short 7-row band; echo the rendered grid until the
    // stack covers the section, so the tilted squares read as a field rather
    // than a single strip. Rows tile seamlessly (see .calendar tr in global.css).
    const echo = () => {
      const field = fieldRef.current;
      const base = calendarRef.current;
      if (!field || !base) return;

      field.querySelectorAll(".calendar-echo").forEach((n) => n.remove());

      // offsetHeight is the pre-transform band; the field's scale lives in CSS,
      // so read it back off the matrix instead of duplicating the number here
      const matrix = new DOMMatrixReadOnly(getComputedStyle(field).transform);
      const scale = Math.hypot(matrix.a, matrix.b) || 1;
      const band = base.offsetHeight * scale;
      if (!band) return;

      // 1.5x the viewport: the tilt needs slack past the top and bottom edges
      const copies = Math.min(8, Math.ceil((window.innerHeight * 1.5) / band) - 1);
      const mid = (copies - 1) / 2;
      for (let i = 0; i < copies; i++) {
        const copy = document.createElement("div");
        copy.className = "calendar calendar-echo";
        copy.innerHTML = base.innerHTML;
        // fall off toward the ends of the stack — reinforces the radial mask's
        // own fade instead of fighting it, and keeps identical clones from
        // reading as an obviously stamped repeat
        const distance = mid ? Math.abs(i - mid) / mid : 0;
        copy.style.opacity = String(1 - distance * 0.55);
        field.appendChild(copy);
      }
    };

    const init = () => {
      if (window.GitHubCalendar && calendarRef.current) {
        // resolves once the grid is fetched and rendered; older builds return
        // undefined, in which case we just keep the single band
        const rendered = window.GitHubCalendar(calendarRef.current, "olisekw", {
          responsive: true,
          tooltips: false,
        });
        if (typeof rendered?.then === "function") {
          rendered.then(echo).catch(() => {});
        }
      }
    };

    if (!document.querySelector(`link[href="${STYLE_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = STYLE_HREF;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (window.GitHubCalendar) {
      init();
    } else if (existing) {
      existing.addEventListener("load", init);
    } else {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.onload = init;
      document.body.appendChild(script);
    }

    // the copy count is viewport-derived, so re-fill when the viewport changes
    let resizeTimer = null;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(echo, 200);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
      if (existing) existing.removeEventListener("load", init);
    };
  }, []);

  const visible = PROJECTS.filter(
    (p) => filter === "All" || p.category === filter
  );

  return (
    <section id="projects" className="section projects">
      {/* full-bleed contribution calendar, tilted into a sharp grid wallpaper
          behind the content; the ref container must stay mounted — the
          widget mutates it */}
      <div className="projects-calendar-bg" aria-hidden="true">
        <div ref={fieldRef} className="calendar-field">
          <div ref={calendarRef} className="calendar" />
        </div>
      </div>

      <p className="section-label">Projects</p>

      <div className="project-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`filter-btn${filter === f ? " selected" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="project-scroller">
        <div className="project-grid">
          {visible.map((p) => {
            return (
              <Link
                key={p.title}
                to={p.route}
                className="project-card"
                aria-label={`${p.title} — ${p.category} project`}
              >
                <div className="card-art" style={{ background: p.art }} />
                <div className="card-body">
                  <p className="card-category">{p.category}</p>
                  <h3>{p.title}</h3>
                  <p className="card-desc">{p.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="github-activity">
        <a href="https://github.com/olisekw" target="_blank" rel="noreferrer">
          github.com/olisekw
        </a>
      </div>
    </section>
  );
}
